import { Page, PageHeader } from "../../components/layout/Page";
import { Box, Button, Checkbox, NumberInput, Select, Switch, Table, Text } from "@mantine/core";
import { FloppyDiskIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useSwr from "swr";
import { LedgerApi } from "../../apis";
import { usePermission } from "../../components/permission";
import { IPostingTemplate, IPostingTemplateLine } from "../../apis/ledger";
import { MoneyCell } from "../../components/list";
import { Notice } from "../../components/ui/notice";
import { StatusBadge } from "../../components/ui/status-badge";
import { currencyFormat } from "../../utils";
import { message } from "../../utils/Message";
import classes from "./templates.module.css";

/** §5.2-ын үүргүүд — сонголтод гарах жагсаалт. */
const ROLE_OPTIONS = [
  "CASH", "BANK:{acc}", "CLEARING:{provider}", "{paymentAccount}",
  "RECV", "RECV:{customer}", "INV", "INV:{warehouse}",
  "PAYABLE", "PAYABLE:{supplier}", "ADV:{customer}",
  "VAT", "REV", "COGS", "EXP:{category}", "INV-ADJUST", "OPENING-EQUITY",
];

/** §5.6-ын контекстийн түлхүүрүүд. */
const AMOUNT_KEYS = ["TOTAL", "SUBTOTAL", "VAT", "PAID", "COST", "ADVANCE", "REMAINDER"];

type SampleContext = { TOTAL: number; SUBTOTAL: number; VAT: number; PAID: number; COST: number };

/**
 * Posting дүрмийн засварлагч (§8.2).
 *
 * ⛔ Аюул A4: дүрэм нь өгөгдөл, код биш. Энэ дэлгэц бол тэр өгөгдлийг
 * засах цорын ганц газар — код дотор ямар ч дүрэм hardcode хийгдээгүй.
 */
export const LedgerTemplatesPage = () => {
  const { t } = useTranslation(["billing", "common"]);
  const canFinance = usePermission("FINANCE").hasAccess;

  const { data, isLoading, mutate } = useSwr<IPostingTemplate[]>(
    "ledger.templates",
    async () => await LedgerApi.getTemplates(),
    { revalidateOnFocus: false },
  );

  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [draft, setDraft] = useState<IPostingTemplateLine[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sample, setSample] = useState<SampleContext>({
    TOTAL: 1_100_000, SUBTOTAL: 1_000_000, VAT: 100_000, PAID: 1_100_000, COST: 700_000,
  });

  const templates = useMemo(() => data ?? [], [data]);
  const selected = templates.find((tpl) => tpl.code === selectedCode) ?? templates[0] ?? null;

  useEffect(() => {
    if (!selected) return;
    setDraft(selected.lines.map((l) => ({ ...l })));
    setIsActive(selected.isActive);
  }, [selected]);

  /**
   * Дэлгэц дээрх урьдчилсан тооцоо. Сервер талд ЯГ ижил дүрмээр дахин
   * шалгагдана — энэ нь зөвхөн засварлаж байхад шууд харагдуулах зорилготой.
   */
  const preview = useMemo(() => {
    const context: Record<string, number> = { ...sample, ADVANCE: 0, REMAINDER: 0 };
    const rows = draft
      .map((line) => ({ line, amount: context[line.amount] ?? Number(line.amount) ?? 0 }))
      .filter(({ line, amount }) => !(line.skipIfZero && amount === 0));

    const debit = rows.filter((r) => r.line.side === "DEBIT").reduce((s, r) => s + r.amount, 0);
    const credit = rows.filter((r) => r.line.side === "CREDIT").reduce((s, r) => s + r.amount, 0);

    return { rows, debit, credit, balanced: Math.abs(debit - credit) < 0.01 };
  }, [draft, sample]);

  const dirty = useMemo(() => {
    if (!selected) return false;
    return isActive !== selected.isActive || JSON.stringify(draft) !== JSON.stringify(selected.lines);
  }, [draft, isActive, selected]);

  const updateLine = (index: number, patch: Partial<IPostingTemplateLine>) =>
    setDraft((lines) => lines.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  const save = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await LedgerApi.saveTemplate(selected.id, { isActive, lines: draft });
      message.success(t("billing:ledger.templateSaved"));
      await mutate();
    } catch (err) {
      message.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const dash = t("common:list.dash");
  const side = (line: IPostingTemplateLine) => line.side === "DEBIT";

  return (
    <Page>
      <PageHeader
        title={t("billing:ledger.templatesTitle")}
        subtitle={t("billing:ledger.templatesSubtitle")}
        extra={
          canFinance ? (
            <Button
              loading={busy}
              disabled={!selected || !dirty || !preview.balanced}
              onClick={save}
              leftSection={<FloppyDiskIcon size={14} />}>
              {t("common:save")}
            </Button>
          ) : undefined
        }
      />

      <Box className={classes.layout}>
        <Box className={classes.card} component="nav" aria-label={t("billing:ledger.templatesTitle")}>
          <Box className={classes.list}>
            {isLoading ? (
              <Text className={classes.state}>{t("common:loading")}</Text>
            ) : templates.map((tpl) => (
              <Box
                key={tpl.id}
                component="button"
                type="button"
                className={`${classes.item} ${selected?.code === tpl.code ? classes.itemActive : ""}`}
                aria-current={selected?.code === tpl.code ? "true" : undefined}
                onClick={() => setSelectedCode(tpl.code)}>
                <Text span className={classes.itemName}>
                  <Text span className={classes.itemNameText}>
                    {t(`billing:ledger.template.${tpl.code}`, { defaultValue: tpl.name })}
                  </Text>
                  {!tpl.isActive && (
                    <StatusBadge color="gray" uppercase={false} label={t("billing:ledger.inactive")} />
                  )}
                </Text>
                <Text span className={classes.itemCode}>{tpl.code}</Text>
              </Box>
            ))}
          </Box>
        </Box>

        {selected && (
          <Box className={classes.main}>
            <Box className={classes.card}>
              <Box className={classes.head}>
                <Box className={classes.headText}>
                  <Text className={classes.headTitle}>
                    {t(`billing:ledger.template.${selected.code}`, { defaultValue: selected.name })}
                  </Text>
                  <Text className={classes.headSub}>{t("billing:ledger.rulesTitle")}</Text>
                </Box>
                <Box className={classes.headRight}>
                  {selected.businessId === null && (
                    <StatusBadge color="gray" uppercase={false} label={t("billing:ledger.defaultRule")} />
                  )}
                  <StatusBadge
                    color={preview.balanced ? "green" : "red"}
                    uppercase={false}
                    label={preview.balanced ? t("billing:ledger.accts.kBalOk") : t("billing:ledger.accts.kBalBad")}
                  />
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.currentTarget.checked)}
                    label={t("billing:ledger.active")}
                    size="sm"
                  />
                </Box>
              </Box>

              <Box className={classes.wrap}>
                <Table className={classes.table} withRowBorders={false} horizontalSpacing={0} verticalSpacing={0}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t("billing:ledger.colSide")}</Table.Th>
                      <Table.Th>{t("billing:ledger.colRole")}</Table.Th>
                      <Table.Th>{t("billing:ledger.colAmount")}</Table.Th>
                      <Table.Th>{t("billing:ledger.colSkipIfZero")}</Table.Th>
                      <Table.Th aria-label="" />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {draft.map((line, index) => (
                      <Table.Tr key={index}>
                        <Table.Td>
                          <Select
                            value={line.side}
                            onChange={(value) => updateLine(index, { side: (value as "DEBIT" | "CREDIT") || "DEBIT" })}
                            data={[
                              { value: "DEBIT", label: t("billing:ledger.debit") },
                              { value: "CREDIT", label: t("billing:ledger.credit") },
                            ]}
                            size="sm"
                            allowDeselect={false}
                            aria-label={t("billing:ledger.colSide")}
                            w={120}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Select
                            value={line.role}
                            onChange={(value) => updateLine(index, { role: value || "REV" })}
                            data={ROLE_OPTIONS.map((r) => ({ value: r, label: r }))}
                            size="sm"
                            searchable
                            aria-label={t("billing:ledger.colRole")}
                            w={220}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Select
                            value={line.amount}
                            onChange={(value) => updateLine(index, { amount: value || "TOTAL" })}
                            data={AMOUNT_KEYS.map((k) => ({ value: k, label: t(`billing:ledger.amount.${k}`, { defaultValue: k }) }))}
                            size="sm"
                            aria-label={t("billing:ledger.colAmount")}
                            w={180}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Checkbox
                            checked={!!line.skipIfZero}
                            onChange={(e) => updateLine(index, { skipIfZero: e.currentTarget.checked })}
                            aria-label={t("billing:ledger.colSkipIfZero")}
                            size="sm"
                          />
                        </Table.Td>
                        <Table.Td ta="right">
                          <Button
                            variant="subtle"
                            color="gray"
                            size="compact-sm"
                            disabled={draft.length <= 2}
                            aria-label={t("common:delete")}
                            onClick={() => setDraft((lines) => lines.filter((_, i) => i !== index))}>
                            <TrashIcon size={14} />
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Box>

              <Box className={classes.foot}>
                <Button
                  variant="default"
                  size="xs"
                  leftSection={<PlusIcon size={13} />}
                  onClick={() => setDraft((lines) => [...lines, { role: "REV", side: "CREDIT", amount: "TOTAL" }])}>
                  {t("billing:ledger.addLine")}
                </Button>
                {preview.balanced && <Text fz="sm" c="dimmed">{t("billing:ledger.rulesBalanced")}</Text>}
              </Box>
              {!preview.balanced && (
                <Box className={classes.footNotice}>
                  <Notice tone="danger" role="alert">
                    {t("billing:ledger.rulesUnbalanced", {
                      debit: currencyFormat(preview.debit),
                      credit: currencyFormat(preview.credit),
                    })}
                  </Notice>
                </Box>
              )}
            </Box>

            <Box className={classes.card}>
              <Box className={classes.head}>
                <Text className={classes.headTitleSm}>{t("billing:ledger.sampleTitle")}</Text>
              </Box>

              <Box className={classes.sampleBody}>
                {(Object.keys(sample) as (keyof SampleContext)[]).map((field) => (
                  <NumberInput
                    key={field}
                    label={t(`billing:ledger.amount.${field}`, { defaultValue: field })}
                    value={sample[field]}
                    onChange={(value) => setSample((s) => ({ ...s, [field]: Number(value) || 0 }))}
                    size="xs"
                    w={140}
                    thousandSeparator=","
                  />
                ))}
              </Box>

              <Box className={classes.wrap}>
                <Table className={classes.table} data-density="compact" withRowBorders={false} horizontalSpacing={0} verticalSpacing={0}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t("billing:ledger.colSide")}</Table.Th>
                      <Table.Th>{t("billing:ledger.colRole")}</Table.Th>
                      <Table.Th ta="right">{t("billing:ledger.colDebit")}</Table.Th>
                      <Table.Th ta="right">{t("billing:ledger.colCredit")}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {preview.rows.map(({ line, amount }, index) => (
                      <Table.Tr key={index}>
                        <Table.Td>
                          <Text span fz="sm" fw={500}>
                            {side(line) ? t("billing:ledger.debitShort") : t("billing:ledger.creditShort")}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text span className={classes.code}>{line.role}</Text>
                        </Table.Td>
                        <Table.Td ta="right">
                          {side(line) ? <MoneyCell amount={amount} /> : <Text span className={classes.dim}>{dash}</Text>}
                        </Table.Td>
                        <Table.Td ta="right">
                          {side(line) ? <Text span className={classes.dim}>{dash}</Text> : <MoneyCell amount={amount} />}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                    <Table.Tr className={classes.subRow}>
                      <Table.Td colSpan={2}>{t("billing:ledger.total")}</Table.Td>
                      <Table.Td ta="right"><MoneyCell amount={preview.debit} emphasis /></Table.Td>
                      <Table.Td ta="right"><MoneyCell amount={preview.credit} emphasis /></Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Page>
  );
};

export default LedgerTemplatesPage;
